<script>
    import moment from 'moment';
    import { __, humanizeDurationVisual, humanizeDurationSpeakable } from '@oat-sa-private/ui-core';
    import { timerLevels } from '@/core/utils/consts.js';

    const durationThrottleConfig = {
        minutesThreshold: 60
    };

    export let data;

    /**
     * Gets the difference between the start and end timestamps.
     * If endTs is not given, sets it to current timestamp.
     * If both startTs and endTs are not given, returns `null`.
     * @param {(string|number)?} startTs - timestamp of the start of the test (RFC3339 or milliseconds)
     * @param {(string|number)?} endTs - timestamp of the end or the current timestamp (RFC3339 or milliseconds)
     * @param {(string|number)?} extra - timestamp for additional time (RFC3339 or milliseconds)
     * @returns {Object|null} `{durationMs: number, extraMs: number?}`
     */
    function getDurationValues(startTs, endTs, extra) {
        if ((startTs === null || typeof startTs === 'undefined') && (endTs === null || typeof endTs === 'undefined')) {
            return null;
        }

        if (typeof endTs === 'string' && endTs.length) {
            endTs = moment(endTs).valueOf();
        }
        if (typeof startTs === 'string' && startTs.length) {
            startTs = moment(startTs).valueOf();
        }
        if (typeof extra === 'string' && extra.length) {
            extra = moment(extra).valueOf();
        }

        if (!(endTs || endTs === 0)) {
            endTs = moment().valueOf();
        }
        if (!(startTs || startTs === 0)) {
            startTs = endTs;
        }

        return { durationMs: endTs - startTs, extraMs: extra };
    }

    /** Get visual and aria labels for duration
     * @param {(string|number)?} startTs - timestamp of the start of the test (RFC3339 or milliseconds)
     * @param {(string|number)?} endTs - timestamp of the end or the current timestamp (RFC3339 or milliseconds)
     * @param {(string|number)?} extra - timestamp for additional time (RFC3339 or milliseconds)
     * @returns {Object} `{visual: string, speakable: string}`
     */
    function getDurationLabels(startTs, endTs, extra) {
        const values = getDurationValues(startTs, endTs, extra);
        if (!values) {
            return { visual: '-', speakable: __('none') };
        }

        const { durationMs, extraMs } = values;
        let visual = humanizeDurationVisual(durationMs, durationThrottleConfig);
        if (extra || extra === 0) {
            visual = `${visual} (+ ${humanizeDurationVisual(extraMs, durationThrottleConfig)})`;
        }
        let speakable = humanizeDurationSpeakable(durationMs, durationThrottleConfig);
        if (extra || extra === 0) {
            speakable = __(
                '%s, additional time %s',
                speakable,
                humanizeDurationSpeakable(extraMs, durationThrottleConfig)
            );
        }
        return { visual, speakable };
    }

    /**
     * @param {String} timerLevel
     * @returns {String} localized timer level
     */
    function getLevelLabel(timerLevel) {
        switch (timerLevel) {
            case timerLevels.test:
                return __('delivery');
            case timerLevels.part:
                return __('part');
            case timerLevels.section:
                return __('section');
            case timerLevels.item:
                return __('question');
            default:
                return '';
        }
    }

    $: durationLabels = getDurationLabels(data.startTs, data.endTs, data.extra);
    $: timerLevelLabel = getLevelLabel(data.timerLevel);
</script>

<style>
    .level {
        color: var(--color-text-feedback);
        font-size: var(--fontsize-body-s);
        margin-top: 0.25rem;
        display: block;
    }
</style>

<span>
    <span class="visually-hidden">{durationLabels.speakable}</span>
    <span aria-hidden="true">{durationLabels.visual}</span>
    {#if timerLevelLabel && data.singleLine} - {timerLevelLabel}{/if}
</span>
{#if timerLevelLabel && !data.singleLine}
    <span class="level">{timerLevelLabel}</span>
{/if}
